import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import { Autocomplete, Chip } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import AsyncLoader from '~components/AsyncLoader';
import { DotLoader } from '~components/DotLoader';
import EmptyState from '~components/EmptyState';
import useDebounce from '~hooks/useDebounce';
import useCampaignListSearch from '~pages/CampaignManagement/CampaignDetails/CampaignLeadLists/useCampaignListSearch';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import CreateLeadListModal from './CreateLeadListModal';
import LeadListCard from './LeadListCard';
import { CreateCampaignLeadList, FilterType } from './useCampaignListSearch/domain';

type FilterOption = { value: FilterType; label: string };
const filterOptions: FilterOption[] = [
  { value: FilterType.Active, label: 'Active' },
  { value: FilterType.Inactive, label: 'Inactive' },
];

const CampaignLeadLists = () => {
  const { hasScope } = useAuth();
  const { pushNotification } = useNotification();
  const { campaignId } = useParams() as { campaignId: string };
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [createModalSubmitting, setCreateModalSubmitting] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(
    filterOptions.filter((i) => i.value === FilterType.Active),
  );
  const filters = selectedFilters.map((i) => i.value);
  const {
    loading,
    error,
    list,
    hasMore,
    create,
    toggleState,
    intersectionObserverRef: lastDataElement,
  } = useCampaignListSearch(+campaignId, {
    search: debouncedSearch,
    filters: filters,
  });

  const toggleCreateListModal = () => {
    setCreateModalOpen((prev) => !prev);
  };

  const addLeadList = async (data: CreateCampaignLeadList) => {
    setCreateModalSubmitting(true);

    try {
      await create(data);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);

      // Modal catches error to prevent form reset on create failure
      return Promise.reject();
    } finally {
      setCreateModalSubmitting(false);
    }

    pushNotification('success', `Created List ${data.name}`);
    setCreateModalOpen(false);
  };

  const displayList = useMemo(
    () =>
      list.map((item, index) => (
        <LeadListCard
          key={item.id}
          ref={index === list.length - 1 ? lastDataElement : null}
          campaignId={+campaignId}
          listId={item.id}
          name={item.name}
          isActive={item.isActive}
          isDefaultList={item.isDefaultList}
          priority={item.priority}
          totalLeads={item.totalLeads}
          numCompleted={item.numCompleted}
          onToggleList={async (checked: boolean) => {
            try {
              await toggleState(item.id, checked);
            } catch (e) {
              pushNotification('error', (e as APIError | UnsupportedStructureError).message);
            }
          }}
        />
      )),
    [list, search, JSON.stringify(filters)],
  );

  // If any filter property is set and displayList is empty we assume no relevant search results
  const noSearchOrFilterResults = (search || filters.length !== 0) && displayList.length === 0;

  return (
    <>
      <Grid sx={{ marginBottom: 1 }} container spacing={1} alignContent='center'>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            variant='outlined'
            label='Search'
            id='search'
            name='search'
            defaultValue={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            multiple
            fullWidth
            filterSelectedOptions
            value={selectedFilters}
            onChange={(_, filters) => {
              setSelectedFilters(filters);
            }}
            options={filterOptions}
            getOptionLabel={(option) => option.label}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  deleteIcon={<CancelIcon />}
                  label={option.label}
                  variant='filled'
                  color='primary'
                />
              ))
            }
            renderInput={(params) => <TextField {...params} label='Filters' variant='outlined' name='filters' />}
          />
        </Grid>

        <Hidden smDown>
          <Grid item md={2}></Grid>
        </Hidden>

        {hasScope(AccessScope.CanCreateLeadsList) && (
          <Grid style={{ display: 'flex', alignItems: 'center' }} item xs={12} md={3}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={toggleCreateListModal}>
              Create Lead List
            </Button>
          </Grid>
        )}
      </Grid>

      <AsyncLoader
        isLoading={(loading && list.length === 0) || search !== debouncedSearch}
        error={
          !noSearchOrFilterResults &&
          list.length === 0 &&
          error && <EmptyState type='error' text='Unexpected Error' subText={error} />
        }>
        <Grid container spacing={1} alignContent='center'>
          <Grid item xs={12}>
            {list.length > 0 && (
              <>
                <List>{displayList}</List>
                {loading && list.length > 0 && <DotLoader align='center' />}

                {!loading && !hasMore && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    No more results to display
                  </Typography>
                )}

                {error && list.length > 0 && (
                  <Typography variant='body2' align='center' color='textSecondary'>
                    Failed to load campaign groups
                  </Typography>
                )}
              </>
            )}

            {list.length === 0 && error && <EmptyState type='error' text='Unexpected Error' subText={error} />}

            {list.length === 0 && !error && noSearchOrFilterResults && (
              <EmptyState
                type='no-records-found'
                text='No lists found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}

            {list.length === 0 && !error && !noSearchOrFilterResults && (
              <EmptyState
                type='no-items-1'
                text='No lead lists currently exist'
                subText='Create a lead list by clicking the button below'
                action={toggleCreateListModal}
                actionText='Create Lead List'
              />
            )}
          </Grid>
        </Grid>
      </AsyncLoader>

      <CreateLeadListModal
        open={createModalOpen}
        submitting={createModalSubmitting}
        onAccept={addLeadList}
        onClose={toggleCreateListModal}
      />
    </>
  );
};

export default CampaignLeadLists;
